// The guides hub's content: one folder per guide (src/content/guides/<slug>/), one Markdown file per language
// (<lang>.md, English first), with front matter (./frontMatter.ts) and a body (./body.ts). This module loads them all
// at build time, checks the front matter and gives the pages, the SEO head and the sitemap one typed list.
//
// Plan names and prices are never typed into a guide: write {{plan.<id>.<field>}} and they're filled in from
// src/content/pricing/plans.ts, so a guide can't quote a price the pricing page doesn't show. Today's plans
// (TODAY_PLANS: payg, lite, pro, enterprise) answer first; the November plans (NEW_PLANS) answer the ids only they
// have (free = Lite, max), and {{newPlan.<free|pro|max>.<field>}} names a November plan explicitly (its Pro differs
// from today's). Fields: name, price, commission, storage, perPhoto. "Max" only ever reaches a guide through a
// token (guides.test.tsx), and the guides hub is staging-only until launch (src/lib/guidesFlag.ts).

import { FREE_DOWNLOAD_OVERAGE, NEW_PLANS, TEAM_LIMITS, TODAY_PLANS } from "@/content/pricing/plans";
import { SEO_LANGS, type SeoLang } from "@/lib/seo/site";
import { GUIDES_PATH } from "@/lib/guidesFlag";
import { findAuthor } from "./authors";
import { blocksText, countWords, htmlComments, parseGuideBody, type GuideBlock } from "./body";
import { splitFrontMatter, type FrontMatter } from "./frontMatter";

export { GUIDES_PATH };

export type GuideAudience = "photographers" | "organisers" | "participants";

/** The hub's sections, in order. */
export const AUDIENCES: { id: GuideAudience; title: string; blurb: string }[] = [
  { id: "photographers", title: "For photographers", blurb: "Pricing, delivery and getting big events online fast." },
  { id: "organisers", title: "For organisers", blurb: "Offering photo search at your event, and doing it properly." },
  { id: "participants", title: "Finding your photos", blurb: "Where your photos are, and how to find yourself in them." },
];

export interface GuideFaq {
  q: string;
  a: string;
}

export interface Guide {
  slug: string;
  lang: SeoLang;
  title: string;
  /** <title> when it should differ from the H1. */
  seoTitle?: string;
  /** Meta description and the hub card's text (≤ 155 characters). */
  description: string;
  /** The first audience is the guide's own (its CTA and breadcrumb); it's listed under every one. */
  audience: GuideAudience[];
  primaryQuery: string;
  related: string[];
  author: string;
  reviewer: string;
  /** ISO dates (YYYY-MM-DD). */
  published: string;
  updated: string;
  lastReviewed: string;
  heroImage: string;
  heroAlt: string;
  heroCaption?: string;
  /** "pending" until the guide has had its legal review (shown on staging, blocked in prod). "owner-approved": published
   *  on the owner's instruction before a legal review (21 Oct 2026); a review is still owed, and "done" records it. */
  legalReview?: "pending" | "done" | "owner-approved";
  faq: GuideFaq[];
  blocks: GuideBlock[];
  /** Body + FAQ words. */
  words: number;
  /** The <!-- … --> notes in the source (TODO(owner) items). */
  notes: string[];
}

/** The figures a plan token can show, from either table. */
interface PlanFigures {
  name: string;
  priceUsd: number;
  commissionRate: number;
  storageGb: number;
  freePhotoUsd: number;
  /** November plans only. */
  freeDownloads?: number;
  /** November plans only; null = unlimited. */
  team?: number | null;
}

const PLAN_FIELDS: Record<string, (p: PlanFigures, lang: string) => string> = {
  name: (p) => p.name,
  /** Free-download photos a month, formatted for the language ("1,000", "1.000"). */
  freeDownloads: (p, lang) => {
    if (p.freeDownloads === undefined) throw new Error(`{{…${p.name}.freeDownloads}} is for the November plans (newPlan)`);
    return p.freeDownloads.toLocaleString(lang);
  },
  /** People in a workspace. Unlimited on Max: write that in words, the token has no value for it. */
  team: (p) => {
    if (p.team === undefined || p.team === null) throw new Error(`{{…${p.name}.team}}: no team limit on this plan`);
    return String(p.team);
  },
  price: (p) => (p.priceUsd === 0 ? "$0" : `$${p.priceUsd.toFixed(2)}`),
  commission: (p) => `${+(p.commissionRate * 100).toFixed(1)}%`,
  storage: (p) => (p.storageGb >= 1000 ? `${p.storageGb / 1000} TB` : `${p.storageGb} GB`),
  perPhoto: (p) => `$${p.freePhotoUsd}`,
};

const todayPlan = (id: string) => {
  const plan = TODAY_PLANS.find((p) => p.id === id);
  if (!plan) throw new Error(`unknown plan "${id}" in a guide token`);
  return plan;
};

const novemberPlan = (id: string): PlanFigures => {
  const plan = NEW_PLANS.find((p) => p.id === id);
  if (!plan) throw new Error(`unknown plan "${id}" in a guide token`);
  return { ...plan, freePhotoUsd: FREE_DOWNLOAD_OVERAGE.perPhotoUsd, team: TEAM_LIMITS[plan.id] };
};

const usd = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;

/** What a plan costs in a month with `sales` (US$) of sales: its monthly price plus its commission on them. */
export const planMonthlyCost = (id: string, sales: number): number => {
  const p = todayPlan(id);
  return Math.round((p.priceUsd + p.commissionRate * sales) * 100) / 100;
};

/** Monthly sales (US$) above which plan `b` costs less than plan `a` (b's higher price, lower commission). */
export const planBreakEven = (a: string, b: string): number => {
  const pa = todayPlan(a);
  const pb = todayPlan(b);
  return Math.round((pb.priceUsd - pa.priceUsd) / (pa.commissionRate - pb.commissionRate));
};

/**
 * Fills in plan figures from plans.ts, so a guide never types a price:
 *   {{plan.<id>.<name|price|commission|storage|perPhoto>}}   today's plan, else the November plan (free, max)
 *   {{newPlan.<free|pro|max>.<field>}}                          the November plan
 *   {{planCost.<id>.<monthly sales in US$>}}   plan price + commission on those sales
 *   {{breakEven.<cheaper plan>.<dearer plan>}}  the monthly sales where the dearer plan starts to cost less
 * An unknown plan or field is an error.
 */
export const fillPlanTokens = (text: string, lang = "en"): string =>
  text
    .replace(/\{\{\s*(plan|newPlan)\.(\w+)\.(\w+)\s*\}\}/g, (_, kind: string, id: string, field: string) => {
      const fmt = PLAN_FIELDS[field];
      if (!fmt) throw new Error(`unknown plan field {{${kind}.${id}.${field}}}`);
      return fmt(kind === "newPlan" ? novemberPlan(id) : TODAY_PLANS.find((p) => p.id === id) ?? novemberPlan(id), lang);
    })
    .replace(/\{\{\s*planCost\.(\w+)\.(\d+)\s*\}\}/g, (_, id: string, sales: string) => usd(planMonthlyCost(id, Number(sales))))
    .replace(/\{\{\s*breakEven\.(\w+)\.(\w+)\s*\}\}/g, (_, a: string, b: string) => usd(planBreakEven(a, b)));

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const str = (fm: FrontMatter, key: string, where: string, optional = false): string | undefined => {
  const v = fm[key];
  if (v === undefined || v === "") {
    if (optional) return undefined;
    throw new Error(`${where}: front matter "${key}" is missing`);
  }
  if (typeof v !== "string") throw new Error(`${where}: front matter "${key}" must be a single value`);
  return v;
};
const list = (fm: FrontMatter, key: string): string[] => {
  const v = fm[key];
  if (v === undefined) return [];
  if (typeof v === "string") return [v];
  return (v as unknown[]).map(String);
};

/** Builds a guide from its file (path is "<slug>/<lang>.md", for errors and the slug/lang check). */
export function buildGuide(path: string, src: string): Guide {
  const m = /([\w-]+)\/([a-z]{2})\.md$/.exec(path);
  if (!m) throw new Error(`${path}: guides live at src/content/guides/<slug>/<lang>.md`);
  const [, folder, lang] = m;
  if (!(SEO_LANGS as readonly string[]).includes(lang)) throw new Error(`${path}: unknown language ${lang}`);
  const { data, body } = splitFrontMatter(src);
  const where = path;
  const slug = str(data, "slug", where)!;
  if (slug !== folder) throw new Error(`${where}: slug "${slug}" doesn't match its folder "${folder}"`);
  const audience = list(data, "audience") as GuideAudience[];
  if (!audience.length || audience.some((a) => !AUDIENCES.some((x) => x.id === a))) throw new Error(`${where}: audience must be from ${AUDIENCES.map((a) => a.id).join(", ")}`);
  const dates = Object.fromEntries(
    (["published", "updated", "lastReviewed"] as const).map((k) => {
      const v = str(data, k, where)!;
      if (!ISO_DATE.test(v)) throw new Error(`${where}: ${k} must be YYYY-MM-DD`);
      return [k, v];
    }),
  ) as Pick<Guide, "published" | "updated" | "lastReviewed">;
  const author = str(data, "author", where)!;
  const reviewer = str(data, "reviewer", where)!;
  for (const id of [author, reviewer]) if (!findAuthor(id)) throw new Error(`${where}: no author "${id}" in authors.ts`);
  const faqRaw = data.faq ?? [];
  if (!Array.isArray(faqRaw) || faqRaw.some((x) => typeof x !== "object")) throw new Error(`${where}: faq must be a list of q/a pairs`);
  const faq = (faqRaw as GuideFaq[]).map((f) => ({ q: fillPlanTokens(f.q), a: fillPlanTokens(f.a) }));
  const legal = str(data, "legalReview", where, true);
  if (legal && legal !== "pending" && legal !== "done" && legal !== "owner-approved") throw new Error(`${where}: legalReview is pending, done or owner-approved`);
  const blocks = parseGuideBody(fillPlanTokens(body));
  return {
    slug,
    lang: lang as SeoLang,
    title: fillPlanTokens(str(data, "title", where)!),
    seoTitle: str(data, "seoTitle", where, true),
    description: fillPlanTokens(str(data, "description", where)!),
    audience,
    primaryQuery: str(data, "primaryQuery", where)!,
    related: list(data, "related"),
    author,
    reviewer,
    ...dates,
    heroImage: str(data, "heroImage", where)!,
    heroAlt: str(data, "heroAlt", where)!,
    heroCaption: str(data, "heroCaption", where, true),
    legalReview: legal as Guide["legalReview"],
    faq,
    blocks,
    words: countWords([...blocksText(blocks), ...faq.flatMap((f) => [f.q, f.a])]),
    notes: htmlComments(src),
  };
}

// Every guide file, as raw text, bundled at build time (server and client render the same content).
const FILES = import.meta.glob("./*/*.md", { query: "?raw", import: "default", eager: true }) as Record<string, string>;

/** Every guide in every language it has. */
export const ALL_GUIDES: Guide[] = Object.entries(FILES)
  .map(([path, src]) => buildGuide(path, src))
  .sort((a, b) => (a.published === b.published ? a.slug.localeCompare(b.slug) : a.published < b.published ? 1 : -1));

/** The guides' slugs, newest first. */
export const GUIDE_SLUGS: string[] = [...new Set(ALL_GUIDES.map((g) => g.slug))];

/** The languages a guide has ("en" first). Empty for an unknown slug. */
export const guideLangs = (slug: string): SeoLang[] => {
  const langs = ALL_GUIDES.filter((g) => g.slug === slug).map((g) => g.lang);
  return SEO_LANGS.filter((l) => langs.includes(l));
};

/** A guide in a language, falling back to English. */
export const getGuide = (slug: string, lang: SeoLang = "en"): Guide | undefined =>
  ALL_GUIDES.find((g) => g.slug === slug && g.lang === lang) ?? ALL_GUIDES.find((g) => g.slug === slug && g.lang === "en");

/** The English guides (the hub lists these). */
export const listGuides = (lang: SeoLang = "en"): Guide[] =>
  GUIDE_SLUGS.map((s) => getGuide(s, lang)).filter((g): g is Guide => !!g);

export const guidePath = (slug: string) => `${GUIDES_PATH}/${slug}`;

/** The hub's own head. */
export const GUIDES_INDEX_SEO = {
  title: "Guides | Photohawk",
  description:
    "Practical guides to event and race photos: how to find yours by bib number or selfie, how to sell them, and how face search works.",
};

/** "18 October 2026" for an ISO date. */
export const formatGuideDate = (iso: string): string => {
  const [y, mo, d] = iso.split("-").map(Number);
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${d} ${months[mo - 1]} ${y}`;
};

/** Minutes to read, at about 230 words a minute. */
export const readingMinutes = (words: number) => Math.max(1, Math.round(words / 230));
